import { DeleteFilled, EditOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Col, Result, Row, Space, Spin, Tooltip, App as AntApp } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { deleteBankAccount, fetchBankAccountById } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BankAccountLookupListDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { modal } = AntApp.useApp();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const isLoading =
        lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.IsFetching ||
        lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching;
    const itemId = params.id ? parseInt(params.id) : undefined;

    const removeBankAccount = () => {
        modal.confirm({
            title: 'Delete bank account',
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this bank account? This action can't be undone.",
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (itemId) dispatch(deleteBankAccount(itemId));
            },
        });
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.bankAccount.deleteStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/bank-account', { replace: true });
        }
    }, [lookupSlice.detailsPage.bankAccount.deleteStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBankAccountById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    if (lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }
    if (!itemId) {
        return <NotFound />;
    }

    return (
        <React.Fragment>
            <ContentHeader
                title={`Bank account #${params.id}`}
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Bank account',
                        path: '/admin/lookup-lists/bank-account',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.bankAccount.data?.name || '-',
                        path: `/admin/lookup-lists/bank-account/${params.id}`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.bankAccount.fetchStatus) && (
                    <>
                        <Button type="primary" danger icon={<DeleteFilled />} loading={isLoading} onClick={() => removeBankAccount()}>
                            Delete option
                        </Button>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/lookup-lists/bank-account/${params.id}/edit`)}
                        >
                            Edit option details
                        </Button>
                    </>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.bankAccount.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.bankAccount.fetchStatus} about={'Bank account'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Bank account Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Id</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.id || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">BBAN</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.bban || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">IBAN</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.iban || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">BIC</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.bic || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">
                                        Currency code{' '}
                                        <Tooltip title="The currency in which this Synthon bank account is held. This is the account currency shown on the payment files, not the currency of the individual payment.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.bankAccountCurrencyCode || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">SEPA</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.sepa ? 'Yes' : 'No'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Default</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.isDefault ? 'Yes' : 'No'}</p>
                                </Col>
                            </Row>

                            {/* ADDRESS INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Address Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Address #1</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.addressline1 || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Address #2</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.addressline2 || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Postal code</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.postalCode || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">City</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.city || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Country</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.country?.description || '-'}</p>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.bankAccount.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankAccount.data?.created
                                            ? dayjs(lookupSlice.detailsPage.bankAccount.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.bankAccount.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankAccount.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.bankAccount.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </React.Fragment>
    );
};
